/**
 * Field — label, control, hint, error.
 *
 * The `error` prop takes the message straight from `react-hook-form`, which in
 * turn takes it from the API's `problem+json` `errors[]` (see
 * `applyProblemErrors`). That chain is the reason a serializer's
 * `{"field": "price", ...}` ends up under the price input and not in a banner
 * nobody reads.
 */
import { useId } from 'react'

import { cn } from './cn'
import { FieldContext, type FieldState } from './fieldContext'

export type FieldProps = {
  label: string
  /** Server- or schema-level message for this field. Presence means invalid. */
  error?: string | undefined
  /** Guidance shown under the control while it is valid. */
  hint?: string | undefined
  required?: boolean | undefined
  /** Hide the label visually but keep it for screen readers. */
  labelHidden?: boolean | undefined
  className?: string | undefined
  children: React.ReactNode
}

export function Field({
  label,
  error,
  hint,
  required = false,
  labelHidden = false,
  className,
  children,
}: FieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  /**
   * ⚠️ **Invalid and unexplained are different states — 22 October 2026.**
   * Since the problem body was reformatted, `errors[]` entries carry only
   * `{field, code}` and the sentence lives in the top-level `detail`, shown
   * once above the form. `applyProblemErrors` therefore sets a single space
   * (`FIELD_INVALID_NO_MESSAGE`) on such a field: enough to outline the input,
   * with nothing to print under it.
   *
   * So invalidity is tested on presence and the *message* on having real
   * characters. Without the split, a space renders an empty `role="alert"` —
   * an announced message with no content, plus a stray gap in the layout.
   */
  const hasMessage = Boolean(error && error.trim())

  const state: FieldState = {
    id,
    // Both, in reading order, so a screen reader gives the guidance and then
    // what went wrong.
    describedBy:
      [hint ? hintId : null, hasMessage ? errorId : null].filter(Boolean).join(' ') || undefined,
    invalid: Boolean(error),
    required,
  }

  return (
    <FieldContext value={state}>
      <div className={cn('space-y-1.5', className)}>
        {/* The asterisk is a *sibling* of the label, not inside it. Inside, it
            becomes part of the label's text — so the control's accessible name
            turns into "Password *", and `getByLabelText('Password')` stops
            matching in every test anyone writes later. */}
        <div className={cn('flex items-center gap-1', labelHidden && 'sr-only')}>
          <label htmlFor={id} className="block text-sm font-medium">
            {label}
          </label>
          {required ? (
            // Marked for sighted users; `aria-required` on the control carries
            // it for everyone else, so this is hidden rather than read as "star".
            <span aria-hidden="true" className="text-danger">
              *
            </span>
          ) : null}
        </div>

        {children}

        {hint ? (
          <p id={hintId} className="text-xs text-fg-muted">
            {hint}
          </p>
        ) : null}

        {hasMessage ? (
          // `role="alert"` so a message that appears after submit is announced
          // rather than silently added to the page.
          <p id={errorId} role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext>
  )
}
